import { Pipe, PipeTransform, inject } from '@angular/core';
import { CurrencyFormatterService } from '../services/currency-formatter.service';

@Pipe({
  name: 'currency',
})
export class CurrencyPipe implements PipeTransform {
  private currencyFormatter = inject(CurrencyFormatterService);

  transform(
    value: number | string | null | undefined,
    currencyCode?: string,
    digitsInfo?: string,
    useAbbreviations: boolean = false,
  ): string {
    if (value === null || value === undefined) {
      return '-';
    }

    let decimals = 2;

    // Extract decimal places from digitsInfo if provided (e.g. "1.2-2")
    if (digitsInfo && typeof digitsInfo === 'string') {
      const match = digitsInfo.match(/\.(\d+)-/);
      if (match) {
        decimals = parseInt(match[1], 10);
      }
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (numValue === 0) {
      return '-';
    }

    const symbol = currencyCode
      ? this.currencyFormatter.getSymbolForCode(currencyCode)
      : this.currencyFormatter.getCurrencySymbol();

    // Handle large numbers with abbreviations (k, m, b) - only if useAbbreviations is true
    if (useAbbreviations) {
      if (numValue >= 1e9) {
        const formattedValue = (numValue / 1e9).toFixed(decimals);
        return `${symbol}${formattedValue}b`;
      } else if (numValue >= 1e6) {
        const formattedValue = (numValue / 1e6).toFixed(decimals);
        return `${symbol}${formattedValue}m`;
      } else if (numValue >= 1e3) {
        const formattedValue = (numValue / 1e3).toFixed(decimals);
        return `${symbol}${formattedValue}k`;
      }
    }

    if (currencyCode) {
      const formatted = this.currencyFormatter.formatCurrency(value, false, decimals);
      return `${symbol}${formatted}`;
    }

    return this.currencyFormatter.formatCurrency(value, true, decimals);
  }
}

@Pipe({
  name: 'formatNumber',
})
export class FormatNumberPipe implements PipeTransform {
  private currencyFormatter = inject(CurrencyFormatterService);

  transform(
    value: number | string | null | undefined,
    decimals: number = 2,
  ): string {
    if (value === null || value === undefined) {
      return '-';
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (numValue === 0) {
      return '-';
    }

    return this.currencyFormatter.formatNumber(value, decimals);
  }
}

/**
 * Summary-board value: shows the full value below a million, and abbreviates
 * from a million up (₦1,750,000 -> ₦1.75M, then B, T). type 'currency' prefixes
 * the tenant currency symbol; 'number' has no symbol.
 */
@Pipe({ name: 'summaryValue' })
export class SummaryValuePipe implements PipeTransform {
  private currencyFormatter = inject(CurrencyFormatterService);

  transform(
    value: number | string | null | undefined,
    type: 'currency' | 'number' = 'currency',
  ): string {
    if (value === null || value === undefined || value === '') return '-';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '-';
    if (num === 0) return type === 'currency' ? '-' : '0';

    const abs = Math.abs(num);
    if (abs >= 1e6) {
      let divisor: number;
      let suffix: string;
      if (abs >= 1e12) {
        divisor = 1e12;
        suffix = 'T';
      } else if (abs >= 1e9) {
        divisor = 1e9;
        suffix = 'B';
      } else {
        divisor = 1e6;
        suffix = 'M';
      }
      const short = (num / divisor).toFixed(2).replace(/\.?0+$/, '');
      const symbol =
        type === 'currency' ? this.currencyFormatter.getCurrencySymbol() : '';
      return `${symbol}${short}${suffix}`;
    }

    return type === 'currency'
      ? this.currencyFormatter.formatCurrency(num, true, 2)
      : this.currencyFormatter.formatNumber(num, 0);
  }
}

@Pipe({ name: 'moneySymbol' })
export class MoneySymbolPipe implements PipeTransform {
  private currencyFormatter = inject(CurrencyFormatterService);
  transform(value: string): any {
    if (!value) return '-';
    const symbol = `${this.currencyFormatter.getCurrencySymbol()}`;
    return `${value} (${symbol})`;
  }
}
@Pipe({ name: 'moneySymbolStandAlone' })
export class MoneySymbolStandAlonePipe implements PipeTransform {
  private currencyFormatter = inject(CurrencyFormatterService);
  transform(_value?: unknown, currencyCode?: string): string {
    if (currencyCode) return this.currencyFormatter.getSymbolForCode(currencyCode);
    return this.currencyFormatter.getCurrencySymbol();
  }
}

@Pipe({ name: 'percentage' })
export class PercentagePipe implements PipeTransform {
  private currencyFormatter = inject(CurrencyFormatterService);

  transform(
    value: number | string | null | undefined,
    decimals: number = 1,
  ): string {
    if (value === null || value === undefined) {
      return '-';
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (numValue === 0) {
      return '0%';
    }

    return this.currencyFormatter.formatPercentage(value, decimals);
  }
}
